/**
 * Pilotage slice — clearing bearings and the plans that group them. It mirrors
 * the routing store (routes + standalone marks): clearing bearings can be a
 * one-off (`planId: null`) or belong to a named pilotage plan, the same way a
 * waypoint can be loose or part of a route. Transits and turns will join the
 * same plan structure later.
 */
import { create } from 'zustand';
import type { ClearingBearing, ClearingDepth, HeadingBearing, PilotagePlan, Transit } from '@/pilotage';

interface PilotageState {
  clearings: ClearingBearing[];
  plans: PilotagePlan[];
  /** The clearing open in the editor / highlighted on the chart, or null. */
  activeClearingId: string | null;
  /** Plan ids hidden from the chart. Empty = all shown. Loose ones always show. */
  hiddenPlanIds: string[];

  addClearing: (cb: ClearingBearing) => void;
  updateClearing: (id: string, patch: Partial<Omit<ClearingBearing, 'id'>>) => void;
  /** Set from a free-end drag: the object-from-vessel bearing and the drawn length. */
  setClearingBearing: (id: string, bearingDeg: number, lengthNm: number) => void;
  removeClearing: (id: string) => void;
  setActiveClearing: (id: string | null) => void;

  /** Create a plan and return its id. */
  addPlan: (name?: string) => string;
  renamePlan: (id: string, name: string) => void;
  /** Remove a plan and the clearing bearings that belong to it. */
  removePlan: (id: string) => void;
  togglePlanHidden: (id: string) => void;
  assignClearingToPlan: (clearingId: string, planId: string | null) => void;

  // Transits (two charted objects in line), grouped into the same plans.
  transits: Transit[];
  activeTransitId: string | null;
  addTransit: (t: Transit) => void;
  updateTransit: (id: string, patch: Partial<Omit<Transit, 'id'>>) => void;
  removeTransit: (id: string) => void;
  setActiveTransit: (id: string | null) => void;
  assignTransitToPlan: (transitId: string, planId: string | null) => void;

  // Heading / back bearings (a single bearing line on one object), same plans.
  headingBearings: HeadingBearing[];
  activeHeadingId: string | null;
  addHeadingBearing: (hb: HeadingBearing) => void;
  updateHeadingBearing: (id: string, patch: Partial<Omit<HeadingBearing, 'id'>>) => void;
  removeHeadingBearing: (id: string) => void;
  setActiveHeadingBearing: (id: string | null) => void;
  assignHeadingToPlan: (headingId: string, planId: string | null) => void;

  // Clearing depths (do-not-cross depth contours), grouped into the same plans.
  clearingDepths: ClearingDepth[];
  activeClearingDepthId: string | null;
  addClearingDepth: (cd: ClearingDepth) => void;
  updateClearingDepth: (id: string, patch: Partial<Omit<ClearingDepth, 'id'>>) => void;
  removeClearingDepth: (id: string) => void;
  setActiveClearingDepth: (id: string | null) => void;
  assignClearingDepthToPlan: (clearingDepthId: string, planId: string | null) => void;
}

let nextC = 1;
let nextP = 1;
export function nextClearingId(): string {
  return `cb-${nextC++}`;
}
export function nextPlanId(): string {
  return `pp-${nextP++}`;
}
let nextT = 1;
export function nextTransitId(): string {
  return `tr-${nextT++}`;
}
let nextH = 1;
export function nextHeadingId(): string {
  return `hb-${nextH++}`;
}
let nextD = 1;
export function nextClearingDepthId(): string {
  return `cd-${nextD++}`;
}

export const usePilotageStore = create<PilotageState>((set) => ({
  clearings: [],
  plans: [],
  activeClearingId: null,
  hiddenPlanIds: [],
  transits: [],
  activeTransitId: null,
  headingBearings: [],
  activeHeadingId: null,
  clearingDepths: [],
  activeClearingDepthId: null,

  addClearing: (cb) => set((s) => ({ clearings: [...s.clearings, cb], activeClearingId: cb.id })),
  updateClearing: (id, patch) =>
    set((s) => ({ clearings: s.clearings.map((c) => (c.id === id ? { ...c, ...patch } : c)) })),
  setClearingBearing: (id, bearingDeg, lengthNm) =>
    set((s) => ({
      clearings: s.clearings.map((c) => (c.id === id ? { ...c, bearingDeg, lengthNm } : c)),
    })),
  removeClearing: (id) =>
    set((s) => ({
      clearings: s.clearings.filter((c) => c.id !== id),
      activeClearingId: s.activeClearingId === id ? null : s.activeClearingId,
    })),
  setActiveClearing: (activeClearingId) => set({ activeClearingId }),

  addPlan: (name) => {
    const id = nextPlanId();
    set((s) => ({ plans: [...s.plans, { id, name: name?.trim() || `Plan ${s.plans.length + 1}` }] }));
    return id;
  },
  renamePlan: (id, name) =>
    set((s) => ({ plans: s.plans.map((p) => (p.id === id ? { ...p, name } : p)) })),
  removePlan: (id) =>
    set((s) => ({
      plans: s.plans.filter((p) => p.id !== id),
      clearings: s.clearings.filter((c) => c.planId !== id),
      transits: s.transits.filter((t) => t.planId !== id),
      headingBearings: s.headingBearings.filter((h) => h.planId !== id),
      clearingDepths: s.clearingDepths.filter((d) => d.planId !== id),
      hiddenPlanIds: s.hiddenPlanIds.filter((x) => x !== id),
    })),
  togglePlanHidden: (id) =>
    set((s) => ({
      hiddenPlanIds: s.hiddenPlanIds.includes(id)
        ? s.hiddenPlanIds.filter((x) => x !== id)
        : [...s.hiddenPlanIds, id],
    })),
  assignClearingToPlan: (clearingId, planId) =>
    set((s) => ({
      clearings: s.clearings.map((c) => (c.id === clearingId ? { ...c, planId } : c)),
    })),

  addTransit: (t) => set((s) => ({ transits: [...s.transits, t], activeTransitId: t.id })),
  updateTransit: (id, patch) =>
    set((s) => ({ transits: s.transits.map((t) => (t.id === id ? { ...t, ...patch } : t)) })),
  removeTransit: (id) =>
    set((s) => ({
      transits: s.transits.filter((t) => t.id !== id),
      activeTransitId: s.activeTransitId === id ? null : s.activeTransitId,
    })),
  setActiveTransit: (activeTransitId) => set({ activeTransitId }),
  assignTransitToPlan: (transitId, planId) =>
    set((s) => ({
      transits: s.transits.map((t) => (t.id === transitId ? { ...t, planId } : t)),
    })),

  addHeadingBearing: (hb) =>
    set((s) => ({ headingBearings: [...s.headingBearings, hb], activeHeadingId: hb.id })),
  updateHeadingBearing: (id, patch) =>
    set((s) => ({
      headingBearings: s.headingBearings.map((h) => (h.id === id ? { ...h, ...patch } : h)),
    })),
  removeHeadingBearing: (id) =>
    set((s) => ({
      headingBearings: s.headingBearings.filter((h) => h.id !== id),
      activeHeadingId: s.activeHeadingId === id ? null : s.activeHeadingId,
    })),
  setActiveHeadingBearing: (activeHeadingId) => set({ activeHeadingId }),
  assignHeadingToPlan: (headingId, planId) =>
    set((s) => ({
      headingBearings: s.headingBearings.map((h) => (h.id === headingId ? { ...h, planId } : h)),
    })),

  addClearingDepth: (cd) =>
    set((s) => ({ clearingDepths: [...s.clearingDepths, cd], activeClearingDepthId: cd.id })),
  updateClearingDepth: (id, patch) =>
    set((s) => ({
      clearingDepths: s.clearingDepths.map((d) => (d.id === id ? { ...d, ...patch } : d)),
    })),
  removeClearingDepth: (id) =>
    set((s) => ({
      clearingDepths: s.clearingDepths.filter((d) => d.id !== id),
      activeClearingDepthId: s.activeClearingDepthId === id ? null : s.activeClearingDepthId,
    })),
  setActiveClearingDepth: (activeClearingDepthId) => set({ activeClearingDepthId }),
  assignClearingDepthToPlan: (clearingDepthId, planId) =>
    set((s) => ({
      clearingDepths: s.clearingDepths.map((d) => (d.id === clearingDepthId ? { ...d, planId } : d)),
    })),
}));
